import React, { useState } from 'react'

import { UseFormReturn } from 'react-hook-form'

import { Calendar as CalendarIcon, X } from 'lucide-react'

import { formatDate, now } from '@/lib/date'
import { cn } from '@/lib/utils'

import { Calendar } from '@/components/ui/calendar'
import { FormField } from '@/components/ui/form'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'

import { SmartButton } from '@/components/ui-custom/SmartButton'

import { FormFieldWrapper } from './FormFieldWrapper'

/**
 * DatePickerField - A date picker form field component
 *
 * IMPORTANT: UTC Date Handling Convention
 * ----------------------------------------
 * This component works with Date objects in the USER'S SELECTED TIMEZONE.
 *
 * When using with API data:
 * 1. When LOADING from API: Convert UTC string to user timezone Date object
 *    Example: defaultValues: { date: fromUTC(apiData.date) }
 *
 * 2. When SUBMITTING to API: Convert Date object to UTC date string
 *    Example: const payload = { date: toUTCDateString(formData.date) }
 *    Note: Use toUTCDateString() for date-only fields (returns "YYYY-MM-DD" in UTC)
 *          Use toUTC() for datetime fields (returns full ISO timestamp)
 *
 * The component automatically:
 * - Converts calendar selections from browser timezone to user's selected timezone
 * - Displays dates using user's timezone and date format preferences
 *
 * @see src/lib/date.ts for conversion utilities (fromUTC, toUTCDateString, toUTC, toUserTimezoneDate)
 * @see .github/copilot-instructions.md for full date handling guide
 */
interface DatePickerFieldProps {
  form: UseFormReturn<any>
  name: string
  label: string
  tooltip?: string
  desc?: string
  required?: boolean
  placeholder?: string
  minDate?: Date
  maxDate?: Date
  disabled?: boolean
  autoFocus?: boolean
  clearable?: boolean
  /** @deprecated Use system date format preference instead. Will be removed in future version. */
  dateFormat?: string
  captionLayout?: 'label' | 'dropdown' | 'dropdown-months' | 'dropdown-years'
  fromYear?: number
  toYear?: number
}

export function DatePickerField({
  form,
  name,
  label,
  tooltip,
  desc,
  required = false,
  placeholder = 'Pick a date',
  minDate,
  maxDate,
  disabled = false,
  autoFocus = false,
  clearable = true,
  dateFormat, // Deprecated: kept for backward compatibility
  captionLayout = 'dropdown',
  fromYear,
  toYear,
}: DatePickerFieldProps) {
  const [open, setOpen] = useState(false)
  const fieldState = form.getFieldState(name)

  const today = now()
  const defaultFromYear = fromYear || today.getFullYear() - 100
  const defaultToYear = toYear || today.getFullYear()

  // Use system date format preference, fallback to custom format for backward compatibility
  const displayFormat = dateFormat ? { style: 'custom' as const, formatStr: dateFormat } : { style: 'short' as const }

  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => (
        <FormFieldWrapper label={label} tooltip={tooltip} desc={desc} required={required}>
          <div className='relative flex items-center'>
            <Popover open={open} onOpenChange={setOpen}>
              <PopoverTrigger asChild>
                <SmartButton
                  variant='outline'
                  className={cn(
                    'w-full justify-start pr-8 text-left font-normal',
                    'aria-invalid:border-destructive aria-invalid:ring-destructive/20',
                    !field.value && 'text-muted-foreground'
                  )}
                  aria-invalid={fieldState.invalid}
                  disabled={disabled}
                  autoFocus={autoFocus}
                >
                  <CalendarIcon className='mr-2 h-4 w-4' />
                  {field.value ? formatDate(field.value, displayFormat) : placeholder}
                </SmartButton>
              </PopoverTrigger>

              <PopoverContent className='w-auto p-0' align='start'>
                <Calendar
                  mode='single'
                  captionLayout={captionLayout}
                  fromYear={defaultFromYear}
                  toYear={defaultToYear}
                  selected={field.value}
                  onSelect={(date) => {
                    // For date-only fields, use the date as-is without timezone conversion
                    // The calendar returns midnight in browser timezone, which is correct for dates
                    field.onChange(date)
                    setOpen(false)
                  }}
                  disabled={(date) => {
                    if (minDate && date < minDate) return true
                    if (maxDate && date > maxDate) return true
                    return false
                  }}
                />
              </PopoverContent>
            </Popover>

            {clearable && field.value && !disabled && (
              <SmartButton
                variant='ghost'
                className='text-muted-foreground hover:text-muted-foreground absolute top-1/2 right-3 flex h-5 w-5 -translate-y-1/2 items-center justify-center hover:bg-transparent'
                onClick={() => {
                  field.onChange(null)
                }}
              >
                <X className='h-3 w-3 stroke-[2]' />
              </SmartButton>
            )}
          </div>
        </FormFieldWrapper>
      )}
    />
  )
}

export const MemoizedDatePickerField = React.memo(DatePickerField) as typeof DatePickerField
